import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Link,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import { Footer } from "./ValueVideo";
import { useNavigate } from "react-router-dom";

// Animated background component (reused from ValueVideo)
const AnimatedBackground = () => (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: -1,
      background:
        "radial-gradient(circle at 50% 50%, #0A1929 0%, #000000 100%)",
      "&::before": {
        content: '""',
        position: "absolute",
        width: "200%",
        height: "200%",
        background:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2000 1500'%3E%3Cdefs%3E%3CradialGradient id='a' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23042644'/%3E%3Cstop offset='1' stop-color='%23000000'/%3E%3C/radialGradient%3E%3ClinearGradient id='b' gradientUnits='userSpaceOnUse' x1='0' y1='750' x2='1550' y2='750'%3E%3Cstop offset='0' stop-color='%23021322'/%3E%3Cstop offset='1' stop-color='%23000000'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23a)' width='2000' height='1500'/%3E%3Crect fill='url(%23b)' width='2000' height='1500'/%3E%3C/svg%3E\")",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        opacity: 0.4,
        animation: "gradient 15s ease infinite",
        transform: "rotate(10deg)",
      },
      "@keyframes gradient": {
        "0%": { transform: "rotate(10deg) scale(1)" },
        "50%": { transform: "rotate(10deg) scale(1.2)" },
        "100%": { transform: "rotate(10deg) scale(1)" },
      },
    }}
  />
);

// Warning Card Component
const WarningCard = ({ children }) => (
  <Box
    sx={{
      background: "rgba(255, 59, 48, 0.1)",
      backdropFilter: "blur(10px)",
      borderRadius: 3,
      border: "1px solid rgba(255, 59, 48, 0.2)",
      p: 4,
      mb: 6,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "4px",
        height: "100%",
        background: "linear-gradient(to bottom, #FF3B30, #FF9500)",
      },
    }}
  >
    {children}
  </Box>
);

// Action Button Component
const ActionButton = ({ children, endIcon, href }) => (
  <Button
    variant="contained"
    fullWidth
    href={href}
    endIcon={endIcon}
    sx={{
      position: "relative",
      overflow: "hidden",
      background: "linear-gradient(45deg, #1976D2, #64B5F6)",
      color: "white",
      fontWeight: 700,
      py: 2.5,
      px: 4,
      borderRadius: 3,
      fontSize: { xs: "1rem", md: "1.1rem" },
      mb: 3,
      boxShadow: "0 10px 20px rgba(25, 118, 210, 0.3)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "-100%",
        width: "100%",
        height: "100%",
        background:
          "linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent)",
        transition: "all 0.5s ease",
      },
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 15px 30px rgba(25, 118, 210, 0.4)",
        "&::before": {
          left: "100%",
        },
      },
      transition: "all 0.3s ease",
    }}
  >
    {children}
  </Button>
);

// Requirements List Component
const RequirementsList = () => {
  const [animate, setAnimate] = useState(false);
  const requirements = [
    // { text: "THIS IS a multiple 4 figure investment", delay: 0 },
    { text: "We have helped MULTIPLE Agents scale already", delay: 100 },
    {
      text: "This will be a decision making call, so make sure to bring your partner/spouse/upline if needed",
      delay: 200,
    },
    
    {
      text: "Please do not book if you do not want to scale your business NOW.",
      delay: 400,
    },
  ];

  useEffect(() => {
    setAnimate(true);
  }, []);

  return (
    <List sx={{ mb: 6 }}>
      {requirements.map((req, index) => (
        <Fade
          key={index}
          in={animate}
          timeout={1000}
          style={{ transitionDelay: `${req.delay}ms` }}
        >
          <ListItem
            sx={{
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(10px)",
              borderRadius: 2,
              mb: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateX(10px)",
                background: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemIcon>
              <CheckCircleIcon sx={{ color: "#64B5F6" }} />
            </ListItemIcon>
            <ListItemText
              primary={req.text}
              sx={{
                "& .MuiTypography-root": {
                  color: "white",
                  fontWeight: 500,
                },
              }}
            />
          </ListItem>
        </Fade>
      ))}
    </List>
  );
};

const CalendlySection = () => {
  
  const navigate = useNavigate(); // React Router hook for navigation

  // Calendly event listeners for tracking and redirection
  useCalendlyEventListener({
    onProfilePageViewed: () => console.log("Profile page viewed"),
    onDateAndTimeSelected: () => console.log("Date and time selected"),
    onEventScheduled: (e) => {
      console.log("Event scheduled:", e.data.payload);
      // Redirect after scheduling an event
      navigate("/thankyou"); // Replace '/thank-you' with your desired route
    },
  });

  return (
    <Box
      sx={{
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        borderRadius: 4,
        border: "1px solid rgba(255, 255, 255, 0.1)",
        overflow: "hidden",
        mb: 8,
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      <InlineWidget
        url="https://calendly.com/kalisunmarketing-sales/30min?hide_event_type_details=1" // Replace with your Calendly URL
        styles={{
          height: "750px",
          padding: "20px 0",
        }}
        pageSettings={{
          backgroundColor: "1a1a1a",
          hideEventTypeDetails: false,
          hideLandingPageDetails: false,
          primaryColor: "1976D2",
          textColor: "ffffff",
        }}
        prefill={{
          email: "",
          firstName: "",
          lastName: "",
          name: "",
        }}
      />
    </Box>
  );
};

// Main Booking Component
const Booking = () => {
  return (
    <Box sx={{ minHeight: "100vh", position: "relative" }}>
      <AnimatedBackground />

      <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 12 }, pb: 8 }}>
        {/* Warning Section */}
        <Fade in timeout={1000}>
          <Box>
            <WarningCard>
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  textAlign: "center",
                  letterSpacing: "0.02em",
                  lineHeight: 1.6,
                }}
              >
                🚨 ATTENTION: DO NOT BOOK A CALL UNLESS YOU’RE SERIOUS ABOUT 10XING YOUR LEAD FLOW <br></br>
                (Our AI Lead Gen System Isn’t for Curious Beginners—It’s for Industry Leaders Ready to Scale)
              </Typography>
            </WarningCard>
          </Box>
        </Fade>

        {/* Action Buttons */}
        <Stack spacing={2} sx={{ mb: 8 }}>
          <ActionButton
            endIcon={<ArrowForwardIcon />}
           
          >
            🚀 EXPLORE THE SYSTEM: How We Generate High-Intent Leads in 48 Hours →
          </ActionButton>
          <ActionButton
            endIcon={<ArrowForwardIcon />}
            
          >
            Free Audit: See How Many Leads You’re Missing Out On
          </ActionButton>
        </Stack>

        {/* Requirements List */}
        <RequirementsList />

        {/* Calendar Loading Note */}
        <Box
          sx={{
            textAlign: "center",
            mb: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <AccessTimeIcon sx={{ color: "#64B5F6" }} />
          <Typography
            variant="body1"
            sx={{
              color: "#B0BEC5",
              fontStyle: "italic",
            }}
          >
            (GIVE THE CALENDAR 2-3 SECONDS TO LOAD)
          </Typography>
        </Box>

        {/* Calendar Component Would Go Here */}
        <Box
          sx={{
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(10px)",
            borderRadius: 4,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            p: 4,
            mb: 8,
            minHeight: "600px", // Placeholder height for calendar
          }}
        >
          {/* Add Calendly component here */}
          <Typography
            variant="body1"
            sx={{
              color: "#B0BEC5",
              textAlign: "center",
            }}
          >
            <CalendlySection />
          </Typography>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default Booking;
